import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline"
import React, { useState } from "react"

import { Button } from "../../components/shared/Buttons"
import { ProfilePageEmptyStateBox } from "../../components/shared/Layout"
import { formatPrice } from "../../utils/utils"

import { useBookableEventTypes } from "./BookableEventTypesContext"
import ScheduledEventsFlyout from "./ScheduledEventsFlyout"

const EventTypeCard = ({ eventType, onEdit, onViewScheduledEvents }) => {
  const isVirtual = eventType.kind !== "in_person"

  // Get the label for the duration unit: min, hour, day. Pluralize if needed.
  const durationUnitLabel = eventType.durationUnit === "minute" ? "min" : eventType.durationUnit
  const durationLabel =
    eventType.duration === 1
      ? `${eventType.duration} ${durationUnitLabel}`
      : `${eventType.duration} ${durationUnitLabel}s`

  return (
    <div className="overflow-hidden rounded-lg border border-gray-light bg-white">
      <div className="flex md:flex-col">
        {/* Event photo */}
        <div className="m-5 h-28 w-28 shrink-0">
          {eventType.photoUrl ? (
            <img src={eventType.photoUrl} alt={eventType.name} className="h-full w-full rounded-lg object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center rounded-lg bg-gray-light">
              <span className="text-gray-dark">No Image</span>
            </div>
          )}
        </div>

        {/* Event details */}
        <div className="flex grow flex-col p-5 md:pt-0 min_md:pl-0">
          <div className="mb-2 flex items-start justify-between">
            <div className="flex items-center">
              <h3 className="text-lg font-medium">{eventType.name}</h3>
              {eventType.url && (
                <a href={eventType.url} target="_blank" rel="noopener noreferrer" className="ml-2 text-gray-dark">
                  <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                </a>
              )}
            </div>
            <Button type="link" className="text-teal" onClick={() => onEdit(eventType)}>
              Edit
            </Button>
          </div>

          <div className="mb-2 text-sm text-gray-dark">
            {durationLabel} • {eventType.amountCents > 0 ? formatPrice(eventType.amountCents) : "Free"} •{" "}
            {eventType.capacity === null ? "Unlimited" : eventType.capacity} Capacity •{" "}
            {isVirtual ? "Virtual" : "In-person"}
            {!isVirtual && eventType.location && <div>{eventType.location}</div>}
          </div>

          <div
            className="wysiwyg-content mb-2 line-clamp-2 text-sm"
            dangerouslySetInnerHTML={{ __html: eventType.description || "No description" }}
          />

          <div className="mt-auto text-sm font-bold text-teal underline">
            {eventType.eventsCount === 1 ? (
              <button className="text-teal hover:text-teal-dark" onClick={() => onViewScheduledEvents(eventType)}>
                1 Scheduled Event
              </button>
            ) : eventType.eventsCount > 1 ? (
              <button className="text-teal hover:text-teal-dark" onClick={() => onViewScheduledEvents(eventType)}>
                {eventType.eventsCount} Scheduled Events
              </button>
            ) : (
              <button
                className="text-teal hover:text-teal-dark"
                onClick={() => {
                  window.location.href = `/portal/calendar?bookableEventTypeId=${eventType.id}#create-group-event`
                }}>
                + Add Event
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

const BookableEventTypesTable = () => {
  const { bookableEventTypes, addEventType, editEventType } = useBookableEventTypes()
  const [selectedEventType, setSelectedEventType] = useState(null)
  const [scheduledEventsFlyoutVisible, setScheduledEventsFlyoutVisible] = useState(false)

  const handleViewScheduledEvents = (eventType) => {
    setSelectedEventType(eventType)
    setScheduledEventsFlyoutVisible(true)
  }

  const closeScheduledEventsFlyout = () => {
    setScheduledEventsFlyoutVisible(false)
  }

  if (bookableEventTypes.length === 0) {
    return (
      <ProfilePageEmptyStateBox onButtonClick={addEventType}>
        You don&apos;t have any event types yet.
      </ProfilePageEmptyStateBox>
    )
  }

  return (
    <div className="mt-6 w-full sm:mt-0">
      <div className="flex flex-col gap-4">
        {bookableEventTypes.map((eventType) => (
          <EventTypeCard
            key={eventType.id}
            eventType={eventType}
            onEdit={editEventType}
            onViewScheduledEvents={handleViewScheduledEvents}
          />
        ))}
      </div>
      <div className="mt-4 flex justify-end">
        <Button onClick={addEventType}>Add New Event Type</Button>
      </div>

      {/* Scheduled Events Flyout */}
      <ScheduledEventsFlyout
        visible={scheduledEventsFlyoutVisible}
        closeFlyout={closeScheduledEventsFlyout}
        eventType={selectedEventType}
        onEditClick={() => {
          editEventType(selectedEventType)
          setScheduledEventsFlyoutVisible(false)
        }}
      />
    </div>
  )
}

export default BookableEventTypesTable
